import React from 'react';
import { Card, CardBody, Typography } from '@one-thd/sui-atomic-components';
import { useConfigService } from '@thd-nucleus/experience-context';
import { usePayments } from '../../../hooks/use-payments';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { useModal } from '../../../hooks/use-modal';
import { MeatballsMenu, } from '../../shared/meatballs-menu/meatballs-menu';
import { PHAlogo } from '../../shared/images/PHAlogo';
import { T2CPendingAlert } from '../t2c-pending-alert/t2c-pending-alert';
import { PHABadge } from '../pro-house-account-badge/PHA-badge';
import { ResendInviteModal, } from '../resend-invite-modal/resend-invite-modal';
import { ERR_UNABLE_TO_RESEND_INVITE } from '../../../constants/response-error-messages';
import { T2CMultiPendingAlert } from '../t2c-multi-pending-alert/t2c-multi-pending-alert';
import { ResendMultiInviteModal, } from '../resend-multi-invite-modal/resend-multi-invite-modal';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { getMultiApproverValues } from '../../../utils/multi-approvers';
import { getHyphenatedPhoneNumber } from '../../../utils/general';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { setResendPendingInvitesAnalytics } from '../../../utils/multi-approvers-analytics';
export const LineOfCredit = ({ shouldDisableProjectLOC, card, onActionFailure, onActionSuccess, isAdmin, isPHAPermissionsEnabled, }) => {
    var _a;
    const { enrollPHAT2C, t2cMultipleApproverContacts } = usePayments();
    const { openModal, setComponentProps, hideModal } = useModal();
    const { locName, cardNumberLast4, locType, paymentId, t2cPrimary, t2cNumber, } = card;
    const { onProHouseAccountRemotePurchase, onProHouseAccountSelectPermissions, } = useMeatballMenuActions();
    const { svocID } = useCustomerInfo();
    const multipleApproversAllowedSvocIDs = useConfigService('T2C_MULTIPLE_APPROVERS_ALLOWED_SVOCIDS');
    const isSvocIDAllowedForMultipleAprovers = typeof multipleApproversAllowedSvocIDs === 'string' &&
        multipleApproversAllowedSvocIDs.includes(svocID);
    const multipleApproversEnabled = !!useConfigService('T2C_MULTIPLE_APPROVERS_ENABLED') ||
        isSvocIDAllowedForMultipleAprovers;
    const { pendingT2CAuthPhoneNumbers, numberOfPendingT2CAuthContacts, numberOfT2CAuthContacts, showT2CMultiPendingAlert, isT2CAuthorized, isT2CPending, } = getMultiApproverValues(t2cMultipleApproverContacts, multipleApproversEnabled, card.t2cStatus);
    const menuItems = [
        {
            value: 'Remote Purchases',
            action: () => {
                onProHouseAccountRemotePurchase(card);
            },
            isHidden: false,
            isDisabled: (card.locType === 'PROJECT' && !!shouldDisableProjectLOC) ||
                ((_a = card.cardStatus) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'inactive',
            dataTestId: 'remote-purchases',
        },
        {
            value: 'Edit User Permissions',
            action: () => {
                onProHouseAccountSelectPermissions(card);
            },
            isHidden: !isAdmin || !isPHAPermissionsEnabled,
            dataTestId: 'edit-user-permissions',
        },
    ];
    const onResendInvite = () => {
        if (!t2cNumber)
            return;
        setComponentProps({ isLoading: true });
        enrollPHAT2C({
            t2cNumber,
            paymentId,
            locType,
            isResend: true,
            t2cApprovers: null,
        })
            .then(() => {
            onActionSuccess(paymentId, `Text2Confirm invite resent to ${getHyphenatedPhoneNumber(t2cNumber)}.`);
        })
            .catch(() => {
            onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
        })
            .finally(() => {
            setComponentProps({ isLoading: false });
            hideModal();
        });
    };
    const openResendInviteModal = () => {
        if (!t2cNumber)
            return;
        const props = {
            isLoading: false,
            t2cNumber,
            onResendInvite,
        };
        openModal(ResendInviteModal, props, { size: 'sm' });
    };
    const onResendMultiInvites = () => {
        if (!pendingT2CAuthPhoneNumbers.length)
            return;
        setComponentProps({ isLoading: true });
        const t2cResendInviteRequest = {
            t2cResendInviteRequest: {
                resendAllPending: true,
                resendInvites: null,
            },
        };
        b2bPaymentsApi
            .multiResendT2CRequest(t2cResendInviteRequest)
            .then(({ data: { success } }) => {
            if (!success) {
                onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
                return;
            }
            setResendPendingInvitesAnalytics(pendingT2CAuthPhoneNumbers.length);
            onActionSuccess(paymentId, `Text2Confirm invite resent to ${pendingT2CAuthPhoneNumbers
                .map((approverT2cNumber) => getHyphenatedPhoneNumber(approverT2cNumber))
                .join(', ')}.`);
        })
            .catch(() => {
            onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
        })
            .finally(() => {
            setComponentProps({ isLoading: false });
            hideModal();
        });
    };
    const openResendMultiInvitesModal = () => {
        if (!pendingT2CAuthPhoneNumbers.length)
            return;
        const props = {
            isLoading: false,
            onResendMultiInvites,
            pendingT2CAuthPhoneNumbers,
        };
        openModal(ResendMultiInviteModal, props, { size: 'sm' });
    };
    return (React.createElement(Card, { className: "sui-h-full" },
        React.createElement(CardBody, null,
            React.createElement("div", { "data-testid": "pro-house-account", className: "sui-flex sui-flex-col sui-gap-3" },
                React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-1" },
                    React.createElement("div", { className: "sui-flex sui-justify-between sui-mb-2" },
                        React.createElement("div", { className: "sui-flex sui-truncate sui-items-center" },
                            React.createElement("h3", { className: "sui-font-display sui-text-3xl sui-mr-4 sui-truncate sui-text-primary" }, locName),
                            locType && React.createElement(PHABadge, { locType: locType })),
                        React.createElement("div", { className: "sui-ml-4" },
                            React.createElement(MeatballsMenu, { label: "Line of Credit", items: menuItems }))),
                    React.createElement("div", { className: "sui-flex sui-flex-wrap sui-mb-4" },
                        React.createElement(PHAlogo, null),
                        React.createElement("div", { className: "sui-ml-4" },
                            React.createElement(Typography, { variant: "body-lg", color: "subtle" },
                                "****",
                                cardNumberLast4))),
                    isT2CAuthorized && (React.createElement(Typography, { color: "primary" },
                        "Text2Confirm Authorized",
                        t2cPrimary && (React.createElement("span", { className: "sui-text-primary" }, " - Primary")))),
                    !showT2CMultiPendingAlert && isT2CPending && t2cNumber && (React.createElement(T2CPendingAlert, { openResendInviteModal: openResendInviteModal })),
                    showT2CMultiPendingAlert && (React.createElement(T2CMultiPendingAlert, { numberOfPendingT2CAuthContacts: numberOfPendingT2CAuthContacts, numberOfContacts: numberOfT2CAuthContacts, onClickResend: openResendMultiInvitesModal })))))));
};
